import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { PageHeader, StatusDot, Empty, CrudActions } from "../components/Crud";
import { ListChecks, Plus } from "lucide-react";

export default function Todo() {
    const { t } = useTranslation();
    const [tasks, setTasks] = useState([]);
    const [events, setEvents] = useState([]);
    const [title, setTitle] = useState("");
    useEffect(() => {
        api.get("/tasks").then(r => setTasks(r.data));
        api.get("/events").then(r => setEvents(r.data));
    }, []);
    const addTask = async (e) => {
        e.preventDefault();
        if (!title) return;
        const { data } = await api.post("/tasks", { title, status: "incomplete" });
        setTasks(x => [data, ...x]);
        setTitle("");
    };
    const update = async (id, body) => {
        const { data } = await api.put(`/tasks/${id}`, body);
        setTasks(x => x.map(t => t.id === id ? data : t));
    };
    const remove = async (id) => { await api.delete(`/tasks/${id}`); setTasks(x => x.filter(t => t.id !== id)); };

    const sections = [
        { key: "incomplete", title: "Incomplete" },
        { key: "in_progress", title: "In progress (50%)" },
        { key: "complete", title: "Complete" },
        { key: "appointment", title: "Appointments / Events" },
    ];

    const grouped = (k) => k === "appointment" ? events : tasks.filter(t => (t.status || "incomplete") === k);

    return (
        <div data-testid="todo-page">
            <PageHeader title={t("pages.todoTitle")} subtitle={t("pages.todoEyebrow")} />
            <form onSubmit={addTask} className="card-warm p-4 flex gap-2 mb-5" data-testid="quick-task-form">
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${t("pages.todoTitle")}…`} data-testid="quick-task-input"
                       className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <button className="btn-primary" data-testid="quick-task-add-btn"><Plus size={14} className="inline" /> {t("pages.todoAdd")}</button>
            </form>
            {tasks.length + events.length === 0 ? <Empty icon={ListChecks} title={t("pages.todoEmpty")} hint={t("pages.todoEmptyHint")} /> :
            <div className="grid md:grid-cols-2 gap-5">
                {sections.map(s => (
                    <div key={s.key} className="card-warm p-5">
                        <div className="font-serif text-xl flex items-center gap-2 mb-3"><StatusDot status={s.key} /> {s.title}</div>
                        <ul className="space-y-1">
                            {grouped(s.key).map(t => (
                                <li key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[rgba(255,107,53,0.06)]">
                                    {s.key !== "appointment" ? (
                                        <select value={t.status} onChange={(e) => update(t.id, { status: e.target.value })} data-testid={`todo-status-${t.id}`}
                                                className="text-xs px-1.5 py-0.5 rounded border border-[rgba(26,19,24,0.12)]">
                                            <option value="incomplete">●</option><option value="in_progress">◐</option><option value="complete">○</option>
                                        </select>
                                    ) : <StatusDot status="appointment" />}
                                    {t.project_id ? (
                                        <Link to={`/projects/${t.project_id}`} className="flex-1 truncate hover:underline">{t.title}</Link>
                                    ) : (
                                        <span className={`flex-1 truncate ${t.status === "complete" ? "line-through text-[var(--text-ink)]/40" : ""}`}>{t.title}</span>
                                    )}
                                    {s.key !== "appointment" && <CrudActions idBase={`td-${t.id}`} onDelete={() => remove(t.id)} />}
                                </li>
                            ))}
                            {grouped(s.key).length === 0 && <li className="text-sm text-[var(--text-ink)]/50">Nothing here.</li>}
                        </ul>
                    </div>
                ))}
            </div>}
        </div>
    );
}
